/**
 * Target colour profiles for every Garaimon.
 *
 * MACHINE GENERATED by `scripts/colour_profiles.py` - do not edit by hand.
 *
 * A profile is the colour mixture a player is aiming for: which colours a
 * creature uses, and in what proportions. Proportions are what matter, so a
 * collection that has all of a creature's colours in the wrong amounts is not
 * a good match.
 *
 * 2 of 78 profiles are measured from sprite art.
 * The rest are placeholders derived from the creature's declared color and
 * accent, marked `source: 'placeholder'`, and must not be read as art.
 *
 * Shares are proportions over the shared anchor palette and sum to 1.
 */

export interface GeneratedColourProfile {
  speciesId: string;
  /** 'sprite' means measured from art; 'placeholder' means derived from two declared colours. */
  source: 'sprite' | 'placeholder';
  /** Which frame a sprite profile was measured from. Absent for placeholders. */
  measuredFrom?: string;
  /** Anchor id and its share. Shares sum to 1. */
  weights: ReadonlyArray<{ anchor: string; hex: string; share: number }>;
}

export const COLOUR_PROFILES_GENERATED: readonly GeneratedColourProfile[] = [
  {
    speciesId: "africa-kibiko",
    source: "placeholder",
    weights: [{ anchor: "a31", hex: "#56c48b", share: 0.780000 }, { anchor: "a40", hex: "#edd077", share: 0.220000 }],
  },
  {
    speciesId: "africa-tumbara",
    source: "placeholder",
    weights: [{ anchor: "a26", hex: "#7e9de5", share: 0.620000 }, { anchor: "a43", hex: "#ebe1c0", share: 0.380000 }],
  },
  {
    speciesId: "africa-nimblet",
    source: "placeholder",
    weights: [{ anchor: "a33", hex: "#70c8cc", share: 0.700000 }, { anchor: "a30", hex: "#ee8c5e", share: 0.300000 }],
  },
  {
    speciesId: "africa-baobloom",
    source: "placeholder",
    weights: [{ anchor: "a39", hex: "#b6d85e", share: 0.550000 }, { anchor: "a43", hex: "#ebe1c0", share: 0.450000 }],
  },
  {
    speciesId: "africa-sundilo",
    source: "placeholder",
    weights: [{ anchor: "a25", hex: "#54aebb", share: 0.840000 }, { anchor: "a37", hex: "#f0a8c3", share: 0.160000 }],
  },
  {
    speciesId: "asia-mizumi",
    source: "placeholder",
    weights: [{ anchor: "a34", hex: "#efaa57", share: 0.660000 }, { anchor: "a30", hex: "#ee8c5e", share: 0.340000 }],
  },
  {
    speciesId: "asia-pandanox",
    source: "placeholder",
    weights: [{ anchor: "a27", hex: "#d77bc2", share: 0.500000 }, { anchor: "a43", hex: "#ebe1c0", share: 0.500000 }],
  },
  {
    speciesId: "asia-kameboom",
    source: "sprite",
    measuredFrom: "sprites/kameboom/spritesheet.png frame 0 (96x96)",
    weights: [{ anchor: "a00", hex: "#11120e", share: 0.265547 }, { anchor: "a05", hex: "#193b6b", share: 0.151119 }, { anchor: "a40", hex: "#edd077", share: 0.121269 }, { anchor: "a02", hex: "#2f241e", share: 0.114428 }, { anchor: "a13", hex: "#2e67b9", share: 0.110075 }, { anchor: "a34", hex: "#efaa57", share: 0.062189 }, { anchor: "a07", hex: "#255a31", share: 0.047886 }, { anchor: "a43", hex: "#ebe1c0", share: 0.040423 }, { anchor: "a04", hex: "#193c1f", share: 0.036692 }, { anchor: "a20", hex: "#b27e51", share: 0.012438 }, { anchor: "a09", hex: "#24508c", share: 0.012438 }, { anchor: "a29", hex: "#cf9f6b", share: 0.011194 }, { anchor: "a06", hex: "#573226", share: 0.008706 }, { anchor: "a12", hex: "#8e5a39", share: 0.005597 }],
  },
  {
    speciesId: "asia-pebblin",
    source: "placeholder",
    weights: [{ anchor: "a22", hex: "#9c80d9", share: 0.580000 }, { anchor: "a38", hex: "#8cd5b5", share: 0.420000 }],
  },
  {
    speciesId: "asia-lotuff",
    source: "placeholder",
    weights: [{ anchor: "a30", hex: "#ee8c5e", share: 0.800000 }, { anchor: "a40", hex: "#edd077", share: 0.200000 }],
  },
  {
    speciesId: "europe-brindlew",
    source: "placeholder",
    weights: [{ anchor: "a26", hex: "#7e9de5", share: 0.780000 }, { anchor: "a37", hex: "#f0a8c3", share: 0.220000 }],
  },
  {
    speciesId: "europe-mallowisp",
    source: "placeholder",
    weights: [{ anchor: "a33", hex: "#70c8cc", share: 0.620000 }, { anchor: "a38", hex: "#8cd5b5", share: 0.380000 }],
  },
  {
    speciesId: "europe-cluckoo",
    source: "sprite",
    measuredFrom: "sprites/cluckoo/spritesheet.png frame 0 (96x96)",
    weights: [{ anchor: "a00", hex: "#11120e", share: 0.338286 }, { anchor: "a43", hex: "#ebe1c0", share: 0.269714 }, { anchor: "a34", hex: "#efaa57", share: 0.089143 }, { anchor: "a23", hex: "#db7767", share: 0.078857 }, { anchor: "a06", hex: "#573226", share: 0.072000 }, { anchor: "a12", hex: "#8e5a39", share: 0.065143 }, { anchor: "a08", hex: "#6e422e", share: 0.036000 }, { anchor: "a40", hex: "#edd077", share: 0.035429 }, { anchor: "a35", hex: "#bfbfb7", share: 0.015429 }],
  },
  {
    speciesId: "europe-thistail",
    source: "placeholder",
    weights: [{ anchor: "a25", hex: "#54aebb", share: 0.550000 }, { anchor: "a36", hex: "#c4b4f0", share: 0.450000 }],
  },
  {
    speciesId: "europe-cloverin",
    source: "placeholder",
    weights: [{ anchor: "a31", hex: "#56c48b", share: 0.840000 }, { anchor: "a34", hex: "#efaa57", share: 0.160000 }],
  },
  {
    speciesId: "north-america-rumblet",
    source: "placeholder",
    weights: [{ anchor: "a27", hex: "#d77bc2", share: 0.660000 }, { anchor: "a40", hex: "#edd077", share: 0.340000 }],
  },
  {
    speciesId: "north-america-pinepup",
    source: "placeholder",
    weights: [{ anchor: "a23", hex: "#db7767", share: 0.500000 }, { anchor: "a36", hex: "#c4b4f0", share: 0.500000 }],
  },
  {
    speciesId: "north-america-cindercub",
    source: "placeholder",
    weights: [{ anchor: "a22", hex: "#9c80d9", share: 0.720000 }, { anchor: "a34", hex: "#efaa57", share: 0.280000 }],
  },
  {
    speciesId: "north-america-mosskunk",
    source: "placeholder",
    weights: [{ anchor: "a30", hex: "#ee8c5e", share: 0.580000 }, { anchor: "a42", hex: "#d5eb9c", share: 0.420000 }],
  },
  {
    speciesId: "north-america-rivermint",
    source: "placeholder",
    weights: [{ anchor: "a34", hex: "#efaa57", share: 0.800000 }, { anchor: "a40", hex: "#edd077", share: 0.200000 }],
  },
  {
    speciesId: "south-america-toucub",
    source: "placeholder",
    weights: [{ anchor: "a33", hex: "#70c8cc", share: 0.780000 }, { anchor: "a34", hex: "#efaa57", share: 0.220000 }],
  },
  {
    speciesId: "south-america-vineva",
    source: "placeholder",
    weights: [{ anchor: "a39", hex: "#b6d85e", share: 0.620000 }, { anchor: "a42", hex: "#d5eb9c", share: 0.380000 }],
  },
  {
    speciesId: "south-america-puddlepod",
    source: "placeholder",
    weights: [{ anchor: "a25", hex: "#54aebb", share: 0.700000 }, { anchor: "a40", hex: "#edd077", share: 0.300000 }],
  },
  {
    speciesId: "south-america-mimibloom",
    source: "placeholder",
    weights: [{ anchor: "a31", hex: "#56c48b", share: 0.550000 }, { anchor: "a43", hex: "#ebe1c0", share: 0.450000 }],
  },
  {
    speciesId: "south-america-canopuff",
    source: "placeholder",
    weights: [{ anchor: "a26", hex: "#7e9de5", share: 0.840000 }, { anchor: "a30", hex: "#ee8c5e", share: 0.160000 }],
  },
  {
    speciesId: "oceania-coraloo",
    source: "placeholder",
    weights: [{ anchor: "a23", hex: "#db7767", share: 0.660000 }, { anchor: "a40", hex: "#edd077", share: 0.340000 }],
  },
  {
    speciesId: "oceania-wavlet",
    source: "placeholder",
    weights: [{ anchor: "a22", hex: "#9c80d9", share: 0.500000 }, { anchor: "a43", hex: "#ebe1c0", share: 0.500000 }],
  },
  {
    speciesId: "oceania-salties",
    source: "placeholder",
    weights: [{ anchor: "a30", hex: "#ee8c5e", share: 1.000000 }],
  },
  {
    speciesId: "oceania-kelpup",
    source: "placeholder",
    weights: [{ anchor: "a34", hex: "#efaa57", share: 0.580000 }, { anchor: "a43", hex: "#ebe1c0", share: 0.420000 }],
  },
  {
    speciesId: "oceania-pearlisk",
    source: "placeholder",
    weights: [{ anchor: "a27", hex: "#d77bc2", share: 0.800000 }, { anchor: "a37", hex: "#f0a8c3", share: 0.200000 }],
  },
  {
    speciesId: "africa-savabex",
    source: "placeholder",
    weights: [{ anchor: "a30", hex: "#ee8c5e", share: 0.780000 }, { anchor: "a43", hex: "#ebe1c0", share: 0.220000 }],
  },
  {
    speciesId: "africa-kilimba",
    source: "placeholder",
    weights: [{ anchor: "a25", hex: "#54aebb", share: 0.620000 }, { anchor: "a40", hex: "#edd077", share: 0.380000 }],
  },
  {
    speciesId: "africa-acaclaw",
    source: "placeholder",
    weights: [{ anchor: "a31", hex: "#56c48b", share: 0.700000 }, { anchor: "a26", hex: "#7e9de5", share: 0.300000 }],
  },
  {
    speciesId: "africa-emberdune",
    source: "placeholder",
    weights: [{ anchor: "a33", hex: "#70c8cc", share: 0.550000 }, { anchor: "a39", hex: "#b6d85e", share: 0.450000 }],
  },
  {
    speciesId: "africa-miragehorn",
    source: "placeholder",
    weights: [{ anchor: "a37", hex: "#f0a8c3", share: 0.840000 }, { anchor: "a30", hex: "#ee8c5e", share: 0.160000 }],
  },
  {
    speciesId: "africa-solmane",
    source: "placeholder",
    weights: [{ anchor: "a40", hex: "#edd077", share: 0.660000 }, { anchor: "a31", hex: "#56c48b", share: 0.340000 }],
  },
  {
    speciesId: "africa-zephyrion",
    source: "placeholder",
    weights: [{ anchor: "a26", hex: "#7e9de5", share: 0.500000 }, { anchor: "a33", hex: "#70c8cc", share: 0.500000 }],
  },
  {
    speciesId: "africa-goldpride",
    source: "placeholder",
    weights: [{ anchor: "a39", hex: "#b6d85e", share: 0.720000 }, { anchor: "a37", hex: "#f0a8c3", share: 0.280000 }],
  },
  {
    speciesId: "asia-bambloo",
    source: "placeholder",
    weights: [{ anchor: "a04", hex: "#193c1f", share: 0.580000 }, { anchor: "a43", hex: "#ebe1c0", share: 0.420000 }],
  },
  {
    speciesId: "asia-jadefin",
    source: "placeholder",
    weights: [{ anchor: "a07", hex: "#255a31", share: 0.800000 }, { anchor: "a34", hex: "#efaa57", share: 0.200000 }],
  },
  {
    speciesId: "asia-koinagi",
    source: "placeholder",
    weights: [{ anchor: "a09", hex: "#24508c", share: 0.780000 }, { anchor: "a40", hex: "#edd077", share: 0.220000 }],
  },
  {
    speciesId: "asia-silkmoth",
    source: "placeholder",
    weights: [{ anchor: "a22", hex: "#9c80d9", share: 0.620000 }, { anchor: "a38", hex: "#8cd5b5", share: 0.380000 }],
  },
  {
    speciesId: "asia-draconis",
    source: "placeholder",
    weights: [{ anchor: "a13", hex: "#2e67b9", share: 0.700000 }, { anchor: "a27", hex: "#d77bc2", share: 0.300000 }],
  },
  {
    speciesId: "asia-lotusveil",
    source: "placeholder",
    weights: [{ anchor: "a38", hex: "#8cd5b5", share: 0.550000 }, { anchor: "a30", hex: "#ee8c5e", share: 0.450000 }],
  },
  {
    speciesId: "asia-jadeemperor",
    source: "placeholder",
    weights: [{ anchor: "a05", hex: "#193b6b", share: 0.840000 }, { anchor: "a34", hex: "#efaa57", share: 0.160000 }],
  },
  {
    speciesId: "asia-qilin",
    source: "placeholder",
    weights: [{ anchor: "a29", hex: "#cf9f6b", share: 0.660000 }, { anchor: "a22", hex: "#9c80d9", share: 0.340000 }],
  },
  {
    speciesId: "europe-fernwick",
    source: "placeholder",
    weights: [{ anchor: "a31", hex: "#56c48b", share: 0.500000 }, { anchor: "a43", hex: "#ebe1c0", share: 0.500000 }],
  },
  {
    speciesId: "europe-mistral",
    source: "placeholder",
    weights: [{ anchor: "a33", hex: "#70c8cc", share: 0.720000 }, { anchor: "a36", hex: "#c4b4f0", share: 0.280000 }],
  },
  {
    speciesId: "europe-glaciel",
    source: "placeholder",
    weights: [{ anchor: "a25", hex: "#54aebb", share: 0.580000 }, { anchor: "a35", hex: "#bfbfb7", share: 0.420000 }],
  },
  {
    speciesId: "europe-thornrose",
    source: "placeholder",
    weights: [{ anchor: "a23", hex: "#db7767", share: 0.800000 }, { anchor: "a37", hex: "#f0a8c3", share: 0.200000 }],
  },
  {
    speciesId: "europe-aurorix",
    source: "placeholder",
    weights: [{ anchor: "a38", hex: "#8cd5b5", share: 0.780000 }, { anchor: "a26", hex: "#7e9de5", share: 0.220000 }],
  },
  {
    speciesId: "europe-stormquill",
    source: "placeholder",
    weights: [{ anchor: "a34", hex: "#efaa57", share: 0.620000 }, { anchor: "a40", hex: "#edd077", share: 0.380000 }],
  },
  {
    speciesId: "europe-caliburn",
    source: "placeholder",
    weights: [{ anchor: "a36", hex: "#c4b4f0", share: 0.700000 }, { anchor: "a33", hex: "#70c8cc", share: 0.300000 }],
  },
  {
    speciesId: "europe-grimoire",
    source: "placeholder",
    weights: [{ anchor: "a00", hex: "#11120e", share: 0.550000 }, { anchor: "a37", hex: "#f0a8c3", share: 0.450000 }],
  },
  {
    speciesId: "north-america-copperfox",
    source: "placeholder",
    weights: [{ anchor: "a34", hex: "#efaa57", share: 0.840000 }, { anchor: "a43", hex: "#ebe1c0", share: 0.160000 }],
  },
  {
    speciesId: "north-america-conifer",
    source: "placeholder",
    weights: [{ anchor: "a22", hex: "#9c80d9", share: 0.660000 }, { anchor: "a42", hex: "#d5eb9c", share: 0.340000 }],
  },
  {
    speciesId: "north-america-canyonhowl",
    source: "placeholder",
    weights: [{ anchor: "a30", hex: "#ee8c5e", share: 0.500000 }, { anchor: "a23", hex: "#db7767", share: 0.500000 }],
  },
  {
    speciesId: "north-america-bisonrock",
    source: "placeholder",
    weights: [{ anchor: "a40", hex: "#edd077", share: 0.720000 }, { anchor: "a22", hex: "#9c80d9", share: 0.280000 }],
  },
  {
    speciesId: "north-america-thunderclaw",
    source: "placeholder",
    weights: [{ anchor: "a36", hex: "#c4b4f0", share: 0.580000 }, { anchor: "a30", hex: "#ee8c5e", share: 0.420000 }],
  },
  {
    speciesId: "north-america-geodeheart",
    source: "placeholder",
    weights: [{ anchor: "a23", hex: "#db7767", share: 0.800000 }, { anchor: "a42", hex: "#d5eb9c", share: 0.200000 }],
  },
  {
    speciesId: "north-america-colossus",
    source: "placeholder",
    weights: [{ anchor: "a27", hex: "#d77bc2", share: 0.780000 }, { anchor: "a34", hex: "#efaa57", share: 0.220000 }],
  },
  {
    speciesId: "north-america-aurorursa",
    source: "placeholder",
    weights: [{ anchor: "a22", hex: "#9c80d9", share: 0.620000 }, { anchor: "a40", hex: "#edd077", share: 0.380000 }],
  },
  {
    speciesId: "south-america-cacaofin",
    source: "placeholder",
    weights: [{ anchor: "a30", hex: "#ee8c5e", share: 0.700000 }, { anchor: "a42", hex: "#d5eb9c", share: 0.300000 }],
  },
  {
    speciesId: "south-america-piranhax",
    source: "placeholder",
    weights: [{ anchor: "a25", hex: "#54aebb", share: 0.550000 }, { anchor: "a31", hex: "#56c48b", share: 0.450000 }],
  },
  {
    speciesId: "south-america-emeraldboa",
    source: "placeholder",
    weights: [{ anchor: "a31", hex: "#56c48b", share: 0.840000 }, { anchor: "a39", hex: "#b6d85e", share: 0.160000 }],
  },
  {
    speciesId: "south-america-quetzal",
    source: "placeholder",
    weights: [{ anchor: "a26", hex: "#7e9de5", share: 0.660000 }, { anchor: "a33", hex: "#70c8cc", share: 0.340000 }],
  },
  {
    speciesId: "south-america-amazonix",
    source: "placeholder",
    weights: [{ anchor: "a39", hex: "#b6d85e", share: 0.500000 }, { anchor: "a43", hex: "#ebe1c0", share: 0.500000 }],
  },
  {
    speciesId: "south-america-sunpetal",
    source: "placeholder",
    weights: [{ anchor: "a40", hex: "#edd077", share: 0.720000 }, { anchor: "a26", hex: "#7e9de5", share: 0.280000 }],
  },
  {
    speciesId: "south-america-rainbowcoatl",
    source: "placeholder",
    weights: [{ anchor: "a33", hex: "#70c8cc", share: 0.580000 }, { anchor: "a39", hex: "#b6d85e", share: 0.420000 }],
  },
  {
    speciesId: "south-america-eldorado",
    source: "placeholder",
    weights: [{ anchor: "a42", hex: "#d5eb9c", share: 0.800000 }, { anchor: "a30", hex: "#ee8c5e", share: 0.200000 }],
  },
  {
    speciesId: "oceania-reefpod",
    source: "placeholder",
    weights: [{ anchor: "a30", hex: "#ee8c5e", share: 0.780000 }, { anchor: "a43", hex: "#ebe1c0", share: 0.220000 }],
  },
  {
    speciesId: "oceania-tidewisp",
    source: "placeholder",
    weights: [{ anchor: "a22", hex: "#9c80d9", share: 0.620000 }, { anchor: "a40", hex: "#edd077", share: 0.380000 }],
  },
  {
    speciesId: "oceania-opalpup",
    source: "placeholder",
    weights: [{ anchor: "a27", hex: "#d77bc2", share: 0.700000 }, { anchor: "a37", hex: "#f0a8c3", share: 0.300000 }],
  },
  {
    speciesId: "oceania-lagoona",
    source: "placeholder",
    weights: [{ anchor: "a23", hex: "#db7767", share: 0.550000 }, { anchor: "a30", hex: "#ee8c5e", share: 0.450000 }],
  },
  {
    speciesId: "oceania-barriax",
    source: "placeholder",
    weights: [{ anchor: "a34", hex: "#efaa57", share: 0.840000 }, { anchor: "a22", hex: "#9c80d9", share: 0.160000 }],
  },
  {
    speciesId: "oceania-cyclonix",
    source: "placeholder",
    weights: [{ anchor: "a40", hex: "#edd077", share: 0.660000 }, { anchor: "a27", hex: "#d77bc2", share: 0.340000 }],
  },
  {
    speciesId: "oceania-leviathan",
    source: "placeholder",
    weights: [{ anchor: "a10", hex: "#555754", share: 0.500000 }, { anchor: "a34", hex: "#efaa57", share: 0.500000 }],
  },
  {
    speciesId: "oceania-southernstar",
    source: "placeholder",
    weights: [{ anchor: "a37", hex: "#f0a8c3", share: 0.720000 }, { anchor: "a23", hex: "#db7767", share: 0.280000 }],
  },
];
